import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/**
 * The console's one button.
 *
 * It replaces 81 hand-written `<button className="h-8 rounded-[7px] …">`
 * sites, each of which had drifted a pixel or a shade from its neighbours.
 * `destructive` is a real variant rather than a ternary because "does this
 * delete something" is the single most important thing a control can say.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive: 'bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90',
        outline: 'border border-border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      /**
       * Four control heights, which is what the v2 design uses and no more:
       * 28px for an action inside a table row, 30px for a chip or a pill,
       * 34px for a toolbar control, 40px for the one primary button on a
       * form. 13px type on the first three — 14px in a 28px control leaves
       * no room above and below the text.
       */
      size: {
        default: 'h-[34px] px-3 text-[13px] has-[>svg]:px-2.5',
        sm: 'h-[30px] gap-1.5 rounded-md px-2.5 text-[13px] has-[>svg]:px-2',
        xs: 'h-7 gap-1.5 rounded-md px-2.5 text-[13px] has-[>svg]:px-2',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-xs': 'size-[26px]',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & { readonly asChild?: boolean };

export function Button({ className, variant, size, asChild = false, ...props }: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button';

  return <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props} />;
}

export { buttonVariants };
